import React, {useCallback, useEffect, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import urlparam from 'urlparam';
import {useEvent} from '../../../../data/hooks/eventHooks';
import {useI18n} from '../../../../data/hooks/i18nHooks';
import {useFilteredParticipants} from '../../../../data/hooks/participant/useFilteredParticipants';
import {useParticipantsCategories} from '../../../../data/hooks/participant/useParticipantsCategories';
import Buttonlink from '../../../elements/ButtonLink';
import CollapsableCategory from '../../../elements/CollapsableCategory/CollapsableCategory';
import Scrollable from '../../../elements/Scrollable';
import SelectableFilterItem from '../../../elements/SelectableFilterItem/SelectableFilterItem';
import TopBarEvent from '../../../elements/TopBar/TopBarEvent';
import Screen from '../../Screen';

import {max, memoize} from 'lodash';
import {useActiveParticipantsFilter} from '../../../../data/hooks/participant/useActiveParticipantsFilter';
import {useRecommendedParticipantsFilter} from '../../../../data/hooks/participant/useRecommendedParticipantsFilter';
import styles from './EventParticipantsFilterScreen.module.scss';
import SmallSpinner from '../../../elements/SmallSpinner/SmallSpinner';

const SAVED_EXPANDED_CATEGORIES = {};

const EventParticipantsFilterScreen = props => {
    const {onReturnClicked} = props,
        {eventId} = useParams(),
        event = useEvent(eventId),
        categories = useParticipantsCategories(event.id),
        [expandedCats, setExpandedCats] = useState((SAVED_EXPANDED_CATEGORIES[event.id] || [categories[0]?.name] || []).filter(i => i)),
        // [prevFilter] = useState(useActiveParticipantsFilter(eventId)),
        currentFilterParams = useActiveParticipantsFilter(eventId),
        [participants, filterParams, busyFiltering] = useFilteredParticipants(event.id),
        recommendedFilter = useRecommendedParticipantsFilter(event.id),
        i18n = useI18n(),
        location = useLocation(),
        history = useHistory();

    // update state from search params
    useEffect(() => {
        const paramCat = urlparam('cat');
        if (paramCat) {
            setExpandedCats([decodeURIComponent(paramCat)]);
            history.replace({search: ''});
        }
    }, [location.search]);

    // if there are no categories, simply redirect to previous screen
    useEffect(() => {
        if (!categories?.length) app.history.replace('..');
    }, []);

    // save expanded/collapsed categories state per event
    useEffect(() => {
        return () => (SAVED_EXPANDED_CATEGORIES[event.id] = expandedCats);
    }, [expandedCats]);

    // Callbacks & event handlers
    const handleApplyFilter = useCallback(() => {
        app.history.replace(['/events', event.id, 'participants'].join('/'));
    }, [filterParams]);

    const handleApplyRecommendedFilter = useCallback(() => {
        if (recommendedFilter) {
            app.store.dispatch(app.actions.participants.applyRecommendedFilter(event.id));
            setExpandedCats(Object.keys(recommendedFilter.categories));
        }
    }, [recommendedFilter]);

    const handleCollapseChange = useCallback(
        (catName, expanded) => {
            if (expanded) {
                setExpandedCats([...expandedCats, catName]);
            } else {
                setExpandedCats(expandedCats.filter(cat => cat != catName));
            }
        },
        [expandedCats]
    );

    const handleSelectionChange = useCallback(
        (cat, option, checked) => {
            if (checked) {
                app.store.dispatch(app.actions.participants.addCategoryFilter(event.id, cat.name, option.name));
            } else {
                app.store.dispatch(app.actions.participants.removeCategoryFilter(event.id, cat.name, option.name));
            }
        },
        [event]
    );

    const getMaxOptionsCount = useCallback(memoize(options => max(options.map(o => o.count ?? 0)) ?? 0));

    // OUTPUT
    const header = (
            <div>
                <TopBarEvent event={event} participants={true} returnURL="..">
                    {i18n['label.filter']}
                </TopBarEvent>
                <hr />
            </div>
        ),
        footer = (
            <div>
                <hr />
                <div className="content">
                    <Buttonlink className={styles.viewResults} onClick={handleApplyFilter}>
                        {i18n['button.view.results']}
                        <span className={styles.count}>{' '}({busyFiltering ? '...' : <span>{participants.length}</span>})</span>
                    </Buttonlink>
                    {recommendedFilter ? (
                        <Buttonlink
                            onClick={handleApplyRecommendedFilter}
                            style={{
                                fontWeight: 'bold',
                                backgroundColor: 'white',
                                border: 0,
                            }}>
                            {i18n['button.filter-recommended']}
                        </Buttonlink>
                    ) : null}
                </div>
            </div>
        );

    return (
        <Screen header={header} footer={footer}>
            <Scrollable>
                {_(categories)
                    .map(cat => {
                        let expanded = _.includes(expandedCats, cat.name),
                            selectedItemsCount = _(filterParams).get(`categories[${cat.name}]`, []).length,
                            title = cat.title || cat.name,
                            subtitle = cat.subtitle;

                        if (!expanded && selectedItemsCount) {
                            // TODO: add missing translation here
                            title = (
                                <span>
                                    {title}
                                    <i
                                        className="marginLeft"
                                        style={{
                                            fontWeight: 'normal',
                                        }}>
                                        [{selectedItemsCount}&nbsp;selected]
                                    </i>
                                </span>
                            );
                        }

                        return (
                            <CollapsableCategory
                                title={title}
                                flagColor={event.settings.primary_color}
                                expanded={expanded}
                                disabled={!cat.options.length}
                                key={cat.name}
                                onChange={expanded => handleCollapseChange(cat.name, expanded)}>
                                {subtitle ? <div className={styles.categorySubtitle}>{subtitle}</div> : null}
                                <ul className="list">
                                    {cat.options.map(option => {
                                        // console.log(option);
                                        let isChecked = currentFilterParams?.categories?.[cat.name]?.includes(option.name) ?? false,
                                            maxCount = getMaxOptionsCount(cat.options);

                                        return (
                                            <li key={option.name}>
                                                <SelectableFilterItem
                                                    checked={isChecked}
                                                    color={option.color}
                                                    count={option.count}
                                                    percentage={maxCount ? option.count / maxCount : 0}
                                                    iconCount="img/member.png"
                                                    onChange={checked => handleSelectionChange(cat, option, checked)}>
                                                    {option.name}
                                                </SelectableFilterItem>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </CollapsableCategory>
                        );
                    })
                    .value()}
            </Scrollable>
        </Screen>
    );
};

export default EventParticipantsFilterScreen;
